import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ageGroupVariationSchema } from "../schemas/ageGroupVariationSchema";
import { db } from "@/services/firebaseService";
import { getDecimalEnums } from "@/services/decimalEnumService";
import { AgeGroupVariationFormData } from "../schemas/ageGroupVariationSchema";
import { buildAgeGroupVariationInitialValues } from "../schemas/ageGroupVariationInitialValues";

export interface GradeOption {
  value: string;
  label: string;
}

export const useAgeGroupVariationForm = (
  defaultValues?: AgeGroupVariationFormData,
  onDirtyChange?: (isDirty: boolean) => void,
  schema?: z.ZodType<AgeGroupVariationFormData>
) => {
  const [loading, setLoading] = useState(true);
  const [gradeOptions, setGradeOptions] = useState<GradeOption[]>([]);

  // Add debugging for incoming defaultValues
  console.log("[useAgeGroupVariationForm] Received defaultValues:", defaultValues);
  console.log("[useAgeGroupVariationForm] regularOfferIncrementAlternatives in defaultValues:", 
    defaultValues?.regularOfferIncrementAlternatives);

  // KB-4281: shared with the operator CLI, so a Shared Program it creates opens with the form's defaults.
  const initialFormValues: AgeGroupVariationFormData = buildAgeGroupVariationInitialValues(defaultValues);

  console.log("[useAgeGroupVariationForm] Final initialFormValues:", initialFormValues);
  console.log("[useAgeGroupVariationForm] regularOfferIncrementAlternatives in initial values:", 
    initialFormValues.regularOfferIncrementAlternatives);

  const form = useForm<AgeGroupVariationFormData>({
    resolver: zodResolver(schema ?? ageGroupVariationSchema),
    defaultValues: initialFormValues,
  });

  // Load grade options
  useEffect(() => {
    const loadGradeOptions = async () => {
      try {
        // Fixed: Call getDecimalEnums with db instance and type filter
        const gradeEnum = await getDecimalEnums(db, "grade");
        
        // Filter out the special grades
        const filteredGradeOptions = gradeEnum
          .filter(item => 
            item.id !== "g_all" && 
            item.id !== "g_unknown" &&
            item.id !== "g_na"
          );
          
        // Sort by the value field
        const sortedGradeOptions = filteredGradeOptions.sort((a, b) => a.value - b.value);
        
        const options: GradeOption[] = [
          { value: "none", label: "None" },
          ...sortedGradeOptions.map(item => ({
            value: item.id,
            label: item.longLabel || item.id
          }))
        ];
        
        setGradeOptions(options);
      } catch (error) {
        console.error("Error loading grade options:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadGradeOptions();
  }, []);

  // Watch for form changes and notify parent component
  useEffect(() => {
    if (onDirtyChange) {
      const subscription = form.watch((value, { name, type }) => {
        onDirtyChange(form.formState.isDirty);
      });
      
      return () => subscription.unsubscribe();
    }
  }, [form, onDirtyChange]);

  return {
    form,
    gradeOptions,
    loading,
  };
};
